import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Match, 
  Team, 
  Player, 
  addMatchEvent, 
  endMatch,
  updateMatchScore,
  deleteMatchEvent,
  getMatch,
  convertToSerializableMatch,
  SerializableMatch,
  resetMatchData,
  subscribeMatchEvents,
  subscribeMatchPeriodState,
  startCurrentPeriod,
  pauseCurrentPeriod,
  switchActivePeriod,
  PeriodKey,
  MatchPeriodState
} from '../../services/matchService';
import { 
  startMatch as startMatchAction, 
  endMatch as endMatchAction, 
  updateScore,
  deleteEvent,
  setCurrentMatch 
} from '../../store/matchSlice';
import { useNavigate } from 'react-router-dom'; // Importer useNavigate

interface LiveMatchControlsProps {
  match: Match | SerializableMatch;
  teamA: Team | null;
  teamB: Team | null;
  matchId?: string; // Ajouter matchId comme prop optionnelle
}

const LiveMatchControls: React.FC<LiveMatchControlsProps> = ({ match, teamA, teamB, matchId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Utiliser useNavigate

  // État persistant du chrono par période (Firestore) + temps affiché
  const [periodState, setPeriodState] = useState<MatchPeriodState | null>(null);
  const [time, setTime] = useState(0); // temps affiché (secs) pour la période active

  // États pour la sélection de joueur et actions
  const [selectedPlayer, setSelectedPlayer] = useState<{
    team: 'teamA' | 'teamB';
    player: Player;
  } | null>(null);

  // Vue active (mobile): A l'écran par défaut on montre l'équipe A; on peut swiper pour afficher l'équipe B
  const [activeTeamView, setActiveTeamView] = useState<'teamA' | 'teamB'>('teamA');
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 40; // px
    if (Math.abs(delta) > threshold) {
      setActiveTeamView(delta < 0 ? 'teamB' : 'teamA');
    }
    touchStartX.current = null;
  };

  // États pour les événements de match
  const [matchEvents, setMatchEvents] = useState<({
    type: string;
    team: 'teamA' | 'teamB';
    player: string;
    minute: number;
    seconds: number;
    period: string;
  })[]>([]);

  // Liste prédéfinie des périodes
  const periodOptions = [
    'Mi-temps 1',
    'Mi-temps 2', 
    'Prolongation 1',
    'Prolongation 2',
    'Tirs au but'
  ];

  // Fonction pour convertir le nom de la période en numéro (déclarée avant usage)
  const getPeriodNumber = (periodName: string): string => {
    switch (periodName) {
      case 'Mi-temps 1':
        return '1';
      case 'Mi-temps 2':
        return '2';
      case 'Prolongation 1':
        return '3';
      case 'Prolongation 2':
        return '4';
      case 'Tirs au but':
        return '5';
      default:
        return '1';
    }
  };

  // État pour le nom de la période
  const [periodName, setPeriodName] = useState<string>(periodOptions[0]);
  // Ref pour stocker le numéro de période courant de manière synchrone
  const periodRef = useRef<string>(getPeriodNumber(periodName));

  // Synchroniser la ref à chaque changement de periodName
  useEffect(() => {
    periodRef.current = getPeriodNumber(periodName);
  }, [periodName]);

  // Abonnement temps réel aux événements du match
  useEffect(() => {
    const currentMatchId = matchId || match?.id;
    if (!currentMatchId) return;
    const unsubscribe = subscribeMatchEvents(currentMatchId, (events) => {
      // Les events sont déjà triés par la requête
      setMatchEvents(events as any);
    });
    return () => unsubscribe();
  }, [matchId, match?.id]);

  // Abonnement à l'état des périodes (Firestore)
  useEffect(() => {
    const currentMatchId = matchId || match?.id;
    if (!currentMatchId) return;
    const unsub = subscribeMatchPeriodState(currentMatchId, (state) => {
      setPeriodState(state);
    });
    return () => unsub();
  }, [matchId, match?.id]);

  // Types d'événements organisés par colonnes
  const leftColumnEvents = [
    { type: 'goal', label: 'But', emoji: '⚽' },
    { type: 'passe_decisive', label: 'Passe D.', emoji: '👥' },
    { type: 'tir_cadre', label: 'T Cadré', emoji: '⚽' },
    { type: 'occasion', label: 'Occas.', emoji: '⚡' },
    { type: 'csc', label: 'CSC', emoji: '❌' }
  ];

  const middleColumnEvents = [
    { type: 'corner', label: 'Corner', emoji: '↗️' },
    { type: 'cfd', label: 'CF Direct', emoji: '🎯' },
    { type: 'cfi', label: 'CF Indirect', emoji: '🎯' },
    { type: 'recup', label: 'Récup.', emoji: '🔄' },
    { type: 'perte', label: 'Perte', emoji: '❌' }
  ];

  const rightColumnEvents = [
    { type: 'hj', label: 'Hors Jeu', emoji: '🚩' },
    { type: 'remplacement', label: 'Change', emoji: '🔄' },
    { type: 'blesse', label: 'Blessé', emoji: '🩺' },
    { type: 'jaune', label: 'Jaune', emoji: '🟨' },
    { type: 'rouge', label: 'Rouge', emoji: '🟥' }
  ];

  // Tableau combiné de tous les événements pour la recherche
  const allEventTypes = [...leftColumnEvents, ...middleColumnEvents, ...rightColumnEvents];

  
  // Calcul local du temps affiché à partir de l'état Firestore
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const compute = () => {
      if (!periodState) return setTime(0);
      const p: PeriodKey = periodRef.current as PeriodKey;
      const base = periodState.periodTimes?.[p] || 0;
      if (periodState.running && periodState.startedAt && periodState.activePeriod === p && p !== '5') {
        const nowMs = Date.now();
        const startMs = periodState.startedAt.toDate().getTime();
        const elapsed = Math.max(0, Math.floor((nowMs - startMs) / 1000));
        setTime(base + elapsed);
      } else {
        setTime(base);
      }
    };
    compute();
    interval = setInterval(() => compute(), 1000);
    return () => interval && clearInterval(interval);
  }, [periodState]);

  // Formater le temps
  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Démarrer le match
  const handleStartMatch = async () => {
    const currentMatchId = matchId || match.id;

    if (!currentMatchId) {
      console.error('[MATCH START] No match ID found', { 
        matchId, 
        match: match.id 
      });
      alert('Impossible de démarrer le match : ID de match manquant');
      return;
    }

    console.log('[MATCH START] Initializing match start', {
      matchId: currentMatchId,
      teamA: teamA?.name,
      teamB: teamB?.name,
      currentTime: new Date().toISOString()
    });

    try {
      // Mettre le match en statut live (Redux) et démarrer la période active uniquement
      dispatch(startMatchAction(currentMatchId));
      const p = periodRef.current as PeriodKey;
      await startCurrentPeriod(currentMatchId, p);
    } catch (error) {
      console.error('Erreur lors du démarrage du match', error);
      // Afficher une notification à l'utilisateur
      alert(`Impossible de démarrer le match : ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  // Mettre en pause
  const handlePauseMatch = async () => {
    const currentMatchId = matchId || match?.id;
    if (!currentMatchId) return;
    console.log('[MATCH PAUSE] Pause current period', {
      currentTime: formatTime(time)
    });
    await pauseCurrentPeriod(currentMatchId);
  };

  // Arrêter le match
  const handleStopMatch = async () => {
    const currentMatchId = matchId || match.id;

    if (!currentMatchId) {
      console.error('[MATCH STOP] No match ID found', { 
        matchId, 
        match: match.id 
      });
      alert('Impossible d\'arrêter le match : ID de match manquant');
      return;
    }

    try {
      // Pause d'abord la période en cours pour accumuler la dernière tranche
      await pauseCurrentPeriod(currentMatchId);
      // Ensuite, marquer le match terminé
      await endMatch(currentMatchId, time);
      
      // Dispatcher l'action Redux
      dispatch(endMatchAction());
      
      // Rediriger vers la page des statistiques
      navigate(`/statistiques?id=${currentMatchId}`);
    } catch (error) {
      console.error('Erreur lors de l\'arrêt du match', error);
    }
  };

  // Ajouter un événement de match
  const handleAddMatchEvent = async (type: string) => {
    console.log('[DEBUG] handleAddMatchEvent called with type:', type);
    console.log('[DEBUG] Current state:', {
      selectedPlayer,
      time,
      matchId,
      match: match?.id,
      currentPeriod: periodName
    });

    const currentMatchId = matchId || match?.id;

    if (!currentMatchId) {
      console.error('[LIVE MATCH CONTROLS] No match ID found', { 
        matchId, 
        match: match?.id,
        matchObject: match
      });
      alert('Impossible d\'ajouter un événement : ID de match manquant');
      return;
    }

    if (!selectedPlayer) {
      console.error('[LIVE MATCH CONTROLS] No player selected');
      alert('Veuillez sélectionner un joueur');
      return;
    }

    try {
      // Ajout de vérifications supplémentaires
      if (!selectedPlayer.player) {
        throw new Error('Informations du joueur manquantes');
      }

      const eventData = {
        type,
        team: selectedPlayer.team,
        player: `${selectedPlayer.player.firstName || ''} ${selectedPlayer.player.lastName || ''}`.trim(),
        minute: Math.floor(time / 60),
        seconds: time % 60,
        period: periodRef.current // Utiliser la ref pour éviter une période obsolète
      };

      console.log('[LIVE MATCH CONTROLS] Preparing to add event', { 
        matchId: currentMatchId, 
        eventData,
        currentScores: {
          teamA: match.scoreA,
          teamB: match.scoreB
        }
      });

      // Ajouter l'événement via le service
      const addedEvent = await addMatchEvent(currentMatchId, eventData);
      console.log('[LIVE MATCH CONTROLS] Event added successfully', { 
        matchId: currentMatchId, 
        addedEvent,
        eventType: type
      });

      // Si c'est un but ou un CSC, mettre à jour le score dans Firestore et Redux
      if (type === 'goal') {
        const newScoreA = selectedPlayer.team === 'teamA' ? match.scoreA + 1 : match.scoreA;
        const newScoreB = selectedPlayer.team === 'teamB' ? match.scoreB + 1 : match.scoreB;
        
        // Mettre à jour le score dans Firestore
        await updateMatchScore(currentMatchId, newScoreA, newScoreB);
        
        // Mettre à jour le score dans Redux
        dispatch(updateScore({ 
          team: selectedPlayer.team, 
          score: selectedPlayer.team === 'teamA' ? newScoreA : newScoreB
        }));
        
        console.log('[LIVE MATCH CONTROLS] Score updated in Firestore and Redux', {
          team: selectedPlayer.team,
          newScoreA,
          newScoreB
        });
      } 
      else if (type === 'csc') {
        // Pour un CSC, on ajoute un but à l'équipe ADVERSE
        const oppositeTeam = selectedPlayer.team === 'teamA' ? 'teamB' : 'teamA';
        const newScoreA = oppositeTeam === 'teamA' ? match.scoreA + 1 : match.scoreA;
        const newScoreB = oppositeTeam === 'teamB' ? match.scoreB + 1 : match.scoreB;
        
        // Mettre à jour le score dans Firestore
        await updateMatchScore(currentMatchId, newScoreA, newScoreB);
        
        // Mettre à jour le score dans Redux
        dispatch(updateScore({ 
          team: oppositeTeam, 
          score: oppositeTeam === 'teamA' ? newScoreA : newScoreB
        }));
        
        console.log('[LIVE MATCH CONTROLS] CSC - Score updated for opposite team in Firestore and Redux', {
          oppositeTeam,
          newScoreA,
          newScoreB
        });
      }
      // Ne pas mettre à jour localement la liste des événements ici.
      // Nous sommes déjà abonnés via subscribeMatchEvents() qui poussera l'événement depuis Firestore.
      // Ajouter localement provoquerait un doublon (optimistic update + onSnapshot).
      
      // Réinitialiser le joueur sélectionné après l'ajout de l'événement
      setSelectedPlayer(null);
    } catch (error) {
      console.error('[LIVE MATCH CONTROLS] Error adding match event', { 
        matchId: currentMatchId, 
        error: error instanceof Error ? error.message : 'Unknown error',
        selectedPlayer,
        time
      });
      alert(`Impossible d'ajouter l'événement : ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  // Sélectionner un joueur
  const handlePlayerSelect = (team: 'teamA' | 'teamB', player: Player) => {
    setSelectedPlayer({ team, player });
  };

  // Rendu des joueurs
  const renderTeamPlayers = (team: Team | null, teamKey: 'teamA' | 'teamB') => {
    if (!team) return null;

    return (
      <div className="grid grid-cols-4 gap-2 mt-4">
        {team.players.map(player => (
          <div key={player.number} className="flex flex-col items-center">
            <button
              onClick={() => handlePlayerSelect(teamKey, player)}
              title={(player.firstName || '').length > 6 ? `${(player.firstName || '').slice(0, 6)}.` : (player.firstName || '')}
              className={`p-2 sm:w-16 sm:h-16 flex items-center justify-center rounded-full text-center font-bold transition-all duration-200 sm:text-lg
                ${selectedPlayer?.player.number === player.number && selectedPlayer.team === teamKey
                  ? (teamKey === 'teamA' 
                      ? 'bg-blue-500 text-white scale-110 shadow-lg' 
                      : 'bg-red-500 text-white scale-110 shadow-lg')
                  : `bg-white border ${teamKey === 'teamA' 
                      ? 'border-blue-200 hover:bg-blue-50' 
                      : 'border-red-200 hover:bg-red-50'} hover:scale-105 shadow-sm`}`}
            >
              {player.number}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* BLOC 0 : Score */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 text-center">
        <div className="flex justify-center items-center space-x-8">
          <div className="text-center">
            <div className="text-xl font-bold mb-2">{teamA?.name || 'Équipe A'}</div>
            <div className="text-7xl font-bold text-blue-600">{match.scoreA}</div>
          </div>
          <div className="text-6xl font-bold text-gray-400">-</div>
          <div className="text-center">
            <div className="text-xl font-bold mb-2">{teamB?.name || 'Équipe B'}</div>
            <div className="text-7xl font-bold text-red-600">{match.scoreB}</div>
          </div>
        </div>
      </div>

      {/* BLOC 1 : Contrôle du Chronomètre */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 text-center">
        {/* Menu déroulant pour la période */}
        <div className="mb-4">
          <select 
            value={periodName}
            onChange={async (e) => {
              console.log('[PERIOD CHANGE] Changing period', {
                oldPeriod: periodName,
                newPeriod: e.target.value,
                periodNumber: getPeriodNumber(e.target.value)
              });
              // Mettre à jour la ref immédiatement pour éviter un clic rapide avec une ancienne valeur
              periodRef.current = getPeriodNumber(e.target.value);
              setPeriodName(e.target.value);
              // Recalcule immédiat du temps affiché pour la période sélectionnée
              try {
                if (periodState) {
                  const p = periodRef.current as PeriodKey;
                  const base = periodState.periodTimes?.[p] || 0;
                  if (
                    periodState.running &&
                    periodState.startedAt &&
                    periodState.activePeriod === p &&
                    p !== '5'
                  ) {
                    const nowMs = Date.now();
                    const startMs = periodState.startedAt.toDate().getTime();
                    const elapsed = Math.max(0, Math.floor((nowMs - startMs) / 1000));
                    setTime(base + elapsed);
                  } else {
                    setTime(base);
                  }
                } else {
                  setTime(0);
                }
              } catch (err) {
                console.warn('[PERIOD CHANGE] compute display time error', err);
              }
              const currentMatchId = matchId || match?.id;
              if (currentMatchId) {
                const p = periodRef.current as PeriodKey;
                await switchActivePeriod(currentMatchId, p);
              }
            }}
            className="w-full text-center text-lg font-semibold border-2 border-gray-300 rounded-md p-2 focus:border-blue-500 outline-none transition-colors"
          >
            {periodOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        {periodRef.current !== '5' && (
          <div className="text-6xl font-bold mb-4 text-gray-800">
            {formatTime(time)}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-4">
          {!(periodState?.running) ? (
            <button 
              onClick={handleStartMatch}
              className="bg-green-500 text-white px-6 py-3 rounded-lg text-xl font-bold hover:bg-green-600 transition-colors shadow-md"
            >
              Démarrer
            </button>
          ) : (
            <React.Fragment>
              <div className="flex flex-wrap justify-center gap-4 w-full">
                <button 
                  onClick={handlePauseMatch}
                  className={`px-6 py-3 rounded-lg text-xl font-bold transition-colors shadow-md bg-yellow-500 hover:bg-yellow-600 text-white`}
                >
                  Pause
                </button>
                <button 
                  onClick={handleStopMatch}
                  className="bg-red-500 text-white px-6 py-3 rounded-lg text-xl font-bold hover:bg-red-600 transition-colors shadow-md"
                >
                  Stop
                </button>
              </div>
            </React.Fragment>
          )}
          {((periodState?.running) || time > 0) && (
            <button 
              onClick={async () => {
                try {
                  const currentMatchId = matchId || match?.id;
                  if (!currentMatchId) {
                    alert('Impossible de réinitialiser : ID de match manquant');
                    return;
                  }

                  if (!window.confirm('Confirmer la réinitialisation complète du match ?\nTous les événements et le score seront remis à zéro.')) {
                    return;
                  }

                  // 1) Réinitialiser en base (Firestore)
                  await resetMatchData(currentMatchId);

                  // 2) Réinitialiser les états locaux
                  setTime(0);
                  setSelectedPlayer(null);
                  setMatchEvents([]);
                  setPeriodName(periodOptions[0]);
                
                  // 3) Recharger le match et mettre à jour Redux
                  const updated = await getMatch(currentMatchId);
                  if (updated) {
                    const serializable = convertToSerializableMatch(updated);
                    dispatch(setCurrentMatch(serializable));
                  }
                } catch (error) {
                  console.error('[LIVE MATCH CONTROLS] Erreur de réinitialisation:', error);
                  alert('Erreur lors de la réinitialisation du match.');
                }
              }}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg text-xl font-bold hover:bg-gray-600 transition-colors shadow-md w-full sm:w-auto"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* BLOC 2 : Sélection de Joueur et Actions */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
        {/* Vue mobile: une équipe à la fois avec swipe */}
        <div className="block sm:hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div className="text-center">
            <h3 className={`text-lg font-bold mb-4 pb-2 border-b-2 ${activeTeamView === 'teamA' ? 'border-blue-300 text-blue-800' : 'border-red-300 text-red-800'}`}>
              {activeTeamView === 'teamA' ? (teamA?.name || 'Équipe A') : (teamB?.name || 'Équipe B')}
            </h3>
            <div className={`rounded-lg p-2 ${activeTeamView === 'teamA' ? 'bg-blue-50' : 'bg-red-50'}`}>
              {activeTeamView === 'teamA'
                ? renderTeamPlayers(teamA, 'teamA')
                : renderTeamPlayers(teamB, 'teamB')}
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
              <button
                onClick={() => setActiveTeamView('teamA')}
                className={`px-3 py-1 rounded ${activeTeamView === 'teamA' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
              >
                {teamA?.name || 'Équipe A'}
              </button>
              <span>Glissez ◀ ▶</span>
              <button
                onClick={() => setActiveTeamView('teamB')}
                className={`px-3 py-1 rounded ${activeTeamView === 'teamB' ? 'bg-red-100 text-red-700' : 'hover:bg-gray-100'}`}
              >
                {teamB?.name || 'Équipe B'}
              </button>
            </div>
          </div>
        </div>

        {/* Vue desktop: deux colonnes inchangées */}
        <div className="hidden sm:grid grid-cols-2 gap-8 relative">
          {/* Séparateur vertical entre les équipes */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 transform -translate-x-1/2"></div>
          {/* Équipe A */}
          <div className="text-center pr-4">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b-2 border-blue-200">{teamA?.name || 'Équipe A'}</h3>
            {renderTeamPlayers(teamA, 'teamA')}
          </div>
          {/* Équipe B */}
          <div className="text-center pl-4">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b-2 border-red-200">{teamB?.name || 'Équipe B'}</h3>
            {renderTeamPlayers(teamB, 'teamB')}
          </div>
        </div>

        {selectedPlayer && (
          <div className="mt-6">
            <div className="grid grid-cols-3 gap-4">
              {/* Colonne de gauche */}
              <div className="space-y-2">
                {leftColumnEvents.map(event => (
                  <button
                    key={event.type}
                    onClick={() => handleAddMatchEvent(event.type)}
                    className={`w-full ${selectedPlayer?.team === 'teamA' ? 'bg-blue-100 hover:bg-blue-200 border-blue-300' : 'bg-red-100 hover:bg-red-200 border-red-300'} text-gray-800 p-3 rounded-lg transition-colors flex flex-col items-center justify-center border`}
                  >
                    <span className="text-2xl">{event.emoji}</span>
                    <span className="text-xs font-bold">{event.label}</span>
                  </button>
                ))}
              </div>
              {/* Colonne du milieu */}
              <div className="space-y-2">
                {middleColumnEvents.map(event => (
                  <button
                    key={event.type}
                    onClick={() => handleAddMatchEvent(event.type)}
                    className={`w-full ${selectedPlayer?.team === 'teamA' ? 'bg-blue-100 hover:bg-blue-200 border-blue-300' : 'bg-red-100 hover:bg-red-200 border-red-300'} text-gray-800 p-3 rounded-lg transition-colors flex flex-col items-center justify-center border`}
                  >
                    <span className="text-2xl">{event.emoji}</span>
                    <span className="text-xs font-bold">{event.label}</span>
                  </button>
                ))}
              </div>
              {/* Colonne de droite */}
              <div className="space-y-2">
                {rightColumnEvents.map(event => (
                  <button
                    key={event.type}
                    onClick={() => handleAddMatchEvent(event.type)}
                    className={`w-full ${selectedPlayer?.team === 'teamA' ? 'bg-blue-100 hover:bg-blue-200 border-blue-300' : 'bg-red-100 hover:bg-red-200 border-red-300'} text-gray-800 p-3 rounded-lg transition-colors flex flex-col items-center justify-center border`}
                  >
                    <span className="text-2xl">{event.emoji}</span>
                    <span className="text-xs font-bold">{event.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BLOC 3 : Fin de Match */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 text-center">
        <button 
          onClick={handleStopMatch}
          className="bg-red-500 text-white px-8 py-4 rounded-lg text-2xl font-bold hover:bg-red-600 transition-colors shadow-lg"
        >
          Terminer le Match
        </button>
      </div>

      {/* BLOC 4 : Tableau des Événements */}
      <div className="mb-0 sm:mb-6 -mx-3 sm:mx-0">
        <h3 className="text-xl font-semibold mb-4 px-3 sm:px-0">Événements</h3>
        
        {matchEvents.length > 0 ? (
          <table className="w-full text-left border-collapse border border-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-center">Tps</th>
                <th className="border p-2 text-center">Équipe</th>
                <th className="border p-2 text-center">Joueur</th>
                <th className="border p-2 text-center">Évé.</th>
                <th className="border p-2 text-center">Pér.</th>
                <th className="border p-2 text-center">Sup.</th>
              </tr>
            </thead>
            <tbody>
              {matchEvents.map((event, index) => {
                const eventTypeInfo = allEventTypes.find(et => et.type === event.type);
                const isMyTeam = event.team === 'teamA'; // Supposons que teamA est votre équipe
                return (
                  <tr 
                    key={(event as any).id || index} 
                    className={`hover:bg-gray-100 
                      ${isMyTeam 
                        ? 'bg-green-50 hover:bg-green-100' 
                        : 'bg-red-50 hover:bg-red-100'}`}
                  >
                    <td className="border p-2 text-center font-mono">
                      {`${event.minute.toString().padStart(2, '0')}'${event.seconds.toString().padStart(2, '0')}`}
                    </td>
                    <td className="border p-2">
                      {event.team === 'teamA' ? teamA?.name : teamB?.name}
                    </td>
                    <td className="border p-2">{event.player}</td>
                    <td className="border p-2 text-center">
                      {eventTypeInfo?.label || event.type}
                    </td>
                    <td className="border p-2">{event.period}</td>
                    <td className="border p-2">
                      <div className="flex justify-center">
                        <button
                          onClick={async () => {
                          if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette action : ${eventTypeInfo?.label} à la ${event.minute}' ?`)) {
                            try {
                              const currentMatchId = matchId || match?.id;
                              if (!currentMatchId) {
                                throw new Error('ID du match non trouvé');
                              }

                              console.log('Suppression de l\'événement...', {
                                matchId: currentMatchId,
                                eventIndex: index,
                                event
                              });

                              // Supprimer l'événement de Firebase et récupérer le match mis à jour
                              await deleteMatchEvent(currentMatchId, index);
                              
                              // Mettre à jour Redux localement (score) sans re-fetch
                              dispatch(deleteEvent({ eventIndex: index }));
                              
                              console.log('Événement supprimé avec succès');
                            } catch (error) {
                              console.error('Erreur lors de la suppression de l\'événement:', error);
                              alert('Erreur lors de la suppression de l\'événement. Veuillez réessayer.');
                            }
                          }
                        }}
                        className="flex items-center justify-center p-1 rounded-full text-red-600 hover:text-red-800 hover:bg-red-100 transition-colors"
                        title="Supprimer cet événement"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-center text-gray-500">Aucun événement pour le moment</p>
        )}
      </div>
    </div>
  );
};

export default LiveMatchControls;
